import apiClient from '@/api/axios';
import { PruefberichtSprache, SpaTexte } from '@/api/model';
import { ApiRouten } from '@/api/routen';
import { de } from 'primelocale/js/de.js';
import { en } from 'primelocale/js/en.js';
import { createI18n } from 'vue-i18n';

export const primeVueLocales = {
    de,
    en
};

/**
 * Registriert einen Callback zum Umschalten der PrimeVue-Locale, der in main.js gesetzt wird.
 * Hintergrund: i18n.ts darf keinen Import aus main.js haben, um zirkuläre Abhängigkeiten zu vermeiden.
 * Deshalb invertieren wir die Abhängigkeit und erwarten hier eine Registrierung aus main.js.
 *
 * WICHTIG: Der Callback muss registriert sein, bevor spracheWechseln(...) oder initI18n() aufgerufen wird.
 * Andernfalls wird absichtlich eine Exception geworfen.
 */
let primeVueLocaleSetter: ((lang: string) => void) | null = null;
export const registerPrimeVueLocaleSetter = (setter: (lang: string) => void) => {
    primeVueLocaleSetter = setter;
};

const primeVueSprachSetzen = (lang: string) => {
    if (!primeVueLocaleSetter) {
        throw new Error('PrimeVue Locale Setter ist nicht registriert. Bitte registerPrimeVueLocaleSetter in main.js aufrufen.');
    }
    primeVueLocaleSetter(lang);
};

const htmlSpracheSetzen = (sprache: string) => {
    // <html lang="...">
    document.documentElement.lang = sprache;
};

// Definiere verfügbare Sprachen
export const verfuegbareSprachen = ['de', 'en'];
const standardsprache = verfuegbareSprachen[0];

const formate = {
    short: { year: '2-digit' as const, month: '2-digit' as const, day: '2-digit' as const },
    time: {
        hour: '2-digit' as const,
        minute: '2-digit' as const,
        hour12: false
    }
};

const datetimeFormats = Object.fromEntries(verfuegbareSprachen.map((lang) => [lang, formate]));

// Erstelle i18n-Instanz mit Composition API
export const i18n = createI18n({
    legacy: false, // Composition API verwenden
    locale: standardsprache,
    fallbackLocale: standardsprache,
    messages: {}, // Leeres Nachrichten-Objekt, wird aus API gefüllt
    globalInjection: true, // $t in Templates verfügbar machen
    datetimeFormats: datetimeFormats as any
});

/**
 * Übersetzungen von der API abrufen
 * @returns Promise, das aufgelöst wird, wenn Übersetzungen geladen sind
 */
const ladeSpaTexte = async (): Promise<void> => {
    const spaTexte = await apiClient.get<SpaTexte[]>(ApiRouten.home().uebersetzungen());

    // Texte in i18n registrieren
    spaTexte.data.forEach((spaText) => {
        i18n.global.setLocaleMessage(spaText.sprache!, spaText.texte!);
    });
};

/**
 * Aktuelle Sprache ändern
 * @param sprache Sprachcode, zu dem gewechselt werden soll
 */
export const spracheWechseln = (sprache: string): void => {
    i18n.global.locale.value = sprache;

    // PrimeVue-Komponenten-Lokalisierung aktualisieren
    primeVueSprachSetzen(sprache);
    htmlSpracheSetzen(sprache);

    // Speichere die Spracheinstellung im localStorage
    localStorage.setItem('sprache', sprache);
};

/**
 * Ermittelt die bevorzugte Sprache des Browsers
 * @returns Die beste verfügbare Sprache basierend auf den Browsereinstellungen
 */
const getBrowserSprache = (): string => {
    const browserSprachen = navigator.languages || [navigator.language || standardsprache];

    // Finde die erste Sprache, die in unseren verfügbaren Sprachen enthalten ist
    for (const sprache of browserSprachen) {
        // Prüfe ob die vollständige Sprache verfügbar ist (z.B. 'en-US')
        if (verfuegbareSprachen.includes(sprache)) {
            return sprache;
        }
        // Prüfe ob die Sprache ohne Ländercode verfügbar ist (z.B. 'en' aus 'en-US')
        const spracheOhneLand = sprache.split('-')[0];
        if (verfuegbareSprachen.includes(spracheOhneLand)) {
            return spracheOhneLand;
        }
    }

    // Falls keine Übereinstimmung gefunden, verwende die Standardsprache
    return standardsprache;
};

/**
 * Initialisiere das Übersetzungssystem
 * @returns Promise, das aufgelöst wird, wenn die Initialisierung abgeschlossen ist
 */
export const initI18n = async (): Promise<void> => {
    // Versuche, die Sprache aus dem localStorage zu holen
    const gespeicherteSprache = localStorage.getItem('sprache');

    // Wenn keine gespeicherte Sprache vorhanden ist, verwende die Browsersprache
    const sprache = gespeicherteSprache ?? getBrowserSprache();

    // Lade Übersetzungen
    await ladeSpaTexte();

    i18n.global.locale.value = sprache;

    // PrimeVue-Lokalisierung aktualisieren
    primeVueSprachSetzen(sprache);
    htmlSpracheSetzen(sprache);
};

// Exportiere die Übersetzungsfunktion für einfachen Zugriff
export const t = i18n.global.t;

export const getSpracheUebersetzt = (praefix: string, sprache: PruefberichtSprache | null | undefined): string => {
    switch (sprache) {
        case PruefberichtSprache.De:
            return t(`${praefix}Sprache_de`);
        case PruefberichtSprache.En:
            return t(`${praefix}Sprache_en`);
        case PruefberichtSprache.Fr:
            return t(`${praefix}Sprache_fr`);
        default:
            throw new Error(`Unbekannte Prüfberichtsprache: ${sprache}`);
    }
};

// Exportiere die Composition API-Funktion
export const useI18n = () => i18n.global;

/**
 * Einfache Methode, die das PrimeVue-Locale der aktuellen Sprache zurückgibt
 * @returns Das PrimeVue-Locale-Objekt der aktuellen Sprache
 */
export const getPrimeVueLocale = () => {
    const currentLocale = i18n.global.locale.value;
    return primeVueLocales[currentLocale as keyof typeof primeVueLocales];
};

export const getRawMessage = (name: string) => {
    const msg = i18n.global.getLocaleMessage(i18n.global.locale.value);

    return msg[name];
};

/**
 * Formatiert ein Datum entsprechend dem aktuellen Locale
 * @param datum Das zu formatierende Datum als String oder null
 * @returns Das formatierte Datum als String oder null
 */
export const getFormatiertesDatum = (datum?: string | Date | null, mitZeit = false): string | null => {
    if (!datum) {
        return null;
    }

    const dateObj = new Date(datum);
    const locale = i18n.global.locale.value;

    return mitZeit
        ? dateObj.toLocaleString(locale, {
              ...formate.short,
              ...formate.time
          })
        : dateObj.toLocaleDateString(locale, formate.short);
};

export const getFormatierteZeit = (datum?: string | Date | null): string | null => {
    if (!datum) {
        return null;
    }

    const dateObj = new Date(datum);
    const locale = i18n.global.locale.value;

    return dateObj.toLocaleTimeString(locale, formate.time);
};

// utils/datum-parse-dmy.ts
// ------------------------------------------------------------
// Unterstützt ausschließlich DMY: Tag/Monat/Jahr
// Erlaubt 1–2-stelligen Tag/Monat und 2- oder 4-stelliges Jahr.
// Beispiele (DE): 1.1.25, 01.01.25, 01.01.2025
// Beispiele (EN, weiterhin DMY!): 1/1/25, 01/01/2025
// ------------------------------------------------------------

/** Locale-Präfix → Datums-Trenner */
export const localeZuTrennerMap: Record<string, string> = {
    de: '.',
    en: '/' // wir bleiben bei D/M/Y, nur der Trenner wechselt
    // bei Bedarf erweitern …
};

/** Regex-escaping für Sonderzeichen (z. B. "." → "\.") */
function escapeFuerRegex(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Erzeugt ein DMY-Regex mit gegebenem Trenner (z. B. "." oder "/") */
export function getDatumRegex(trenner: string): RegExp {
    const sep = escapeFuerRegex(trenner);
    // ^(Tag)(sep)(Monat)(sep)(Jahr)$  — Tag/Monat: 1–2-stellig; Jahr: 2 oder 4
    return new RegExp(`^(\\d{1,2})${sep}(\\d{1,2})${sep}(\\d{2}|\\d{4})$`);
}

/** Pivot-Logik: 00–69 → 2000–2069, 70–99 → 1900–1999 (anpassbar) */
export function normalisiereZweistelligesJahr(y: number, pivot = 70): number {
    if (y >= 100) return y;
    return y >= pivot ? 1900 + y : 2000 + y;
}

/**
 * Parst ein Datum im D/M/Y-Format mit locale-abhängigem Trenner.
 *
 * @param text   Eingabe wie "01.01.25", "1.1.25", "01/01/2025"
 * @returns      Date oder null (bei ungültig)
 *
 * Beispiele:
 *  parseDatumDMY("01.01.2025", "de-DE") → 2025-01-01T00:00:00
 *  parseDatumDMY("1.1.25",     "de-DE") → 2025-01-01T00:00:00
 *  parseDatumDMY("01/01/25",   "en-US") → 2025-01-01T00:00:00
 *  parseDatumDMY("31.02.2025", "de-DE") → null (ungültig)
 */
export function parseDatum(text: string | null): Date | null {
    if (!text) {
        return null;
    }

    const trenner = localeZuTrennerMap[i18n.global.locale.value];
    const regex = getDatumRegex(trenner);

    const cleaned = text.trim().replace(/\s+/g, '');
    const matcher = cleaned.match(regex);
    if (!matcher) {
        return null;
    }

    const tag = Number(matcher[1]);
    const monatIndex = Number(matcher[2]) - 1; // JS: 0–11
    let jahr = Number(matcher[3]);

    if (jahr < 100) {
        jahr = normalisiereZweistelligesJahr(jahr);
    }

    // Harte Validierung (fängt 31.02.xxxx etc. ab)
    const datum = new Date(jahr, monatIndex, tag);
    const istGueltig = datum.getFullYear() === jahr && datum.getMonth() === monatIndex && datum.getDate() === tag;

    return istGueltig ? datum : null;
}

export const parseZeit = (time: string): Date | null => {
    // hh:mm
    if (!/^\d{1,2}:\d{1,2}$/.test(time)) {
        return null;
    }

    const [h, m] = time.split(':').map(Number);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        var datum = new Date();
        datum.setHours(h, m, 0, 0);

        return datum;
    }

    return null;
};

// In PrimeVue gilt: y = zweistelliges Jahr, yy = vierstelliges Jahr.
export function getPrimeVueDateFormat() {
    switch (i18n.global.locale.value) {
        case 'en':
            return 'dd/mm/y';
        case 'de':
            return 'dd.mm.y';
    }
}
